import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Party } from '../models/party';
import { PartyType } from '../models/party-type';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { User } from '../../auth/models/User';
import { PropertyLookupStore } from '../../shared/lookup/property-lookup/property-lookup.store';
import { VendorPopupComponent } from '../vendor-popup/vendor-popup.component';
import { TranslateService } from '@ngx-translate/core';
import { LogService } from '../../shared/log/log.service';
import { AuthenticationStore } from '../../permission/services/authentication.store';
import { PropertyLookupDetailQuery } from '../../shared/models/property-lookup-detail-query';
import { Contact } from '../models/contact';
import { PioneerValidators } from '../../shared/utils/pioneer.validators';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';
import { EventStore } from '../service/event.store';
import { OnyxEvent } from '../models/event';
import { EventValidationService } from '../service/event-validation.service';
import { EnvironmentConfiguration } from 'app/environment/services/environment.configuration';
import { Subject } from 'rxjs/Subject';
import { take } from 'rxjs/operators';

@Component({
  selector: 'pioneer-vendor-card-detail',
  templateUrl: './vendor-card-detail.component.html',
  styleUrls: ['./vendor-card-detail.component.css']
})
export class VendorCardDetailComponent implements OnInit, OnDestroy {

  @Input() vendor: Party;
  @Input() parentForm: FormGroup;
  @Input() isReadOnly = false;
  @Input() isNewEvent = false;
  @Input() inputEmail: string;
  @Input() isCardOpen = false;

  @Output() deleteVendor: EventEmitter<Party> = new EventEmitter<Party>();
  @Output() outputEmail: EventEmitter<string> = new EventEmitter<string>();
  @Output() onToggleCard = new EventEmitter<void>();

  @ViewChild('confirmDeleteModal') confirmDeleteModal: ConfirmationModalComponent;
  @ViewChild(VendorPopupComponent) propertyPopup: VendorPopupComponent;
  @ViewChild('cardWrapper') cardWrapper: ElementRef;

  cardTitle: string;
  partyType = PartyType.PROPERTY;
  contactLookupForm: FormGroup;
  contactReferenceNumberTitle: string;
  isContactEditable = true;
  showSubscriberBadge = false;
  canRemoveVendor = false;
  canEditVendorCommRate = false;
  grouppayLogo: string;
  eventId: number;
  showLongLabels = false;

  private _destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private formBuilder: FormBuilder,
              private translateService: TranslateService,
              private propertyLookupStore: PropertyLookupStore,
              private logService: LogService,
              private authenticationStore: AuthenticationStore,
              private eventStore: EventStore,
              private environment: EnvironmentConfiguration,
              private eventValidationService: EventValidationService) {
  }

  ngOnInit() {
    this.grouppayLogo = `${this.environment.onyxGPImagesCommonDataUrl}/grouppay-logo-symbol.png`;

    this.contactReferenceNumberTitle = this.translateService.instant('EVENT.CONTACT_PROPERTY_REF_CODE');

    this.contactLookupForm = this.dataToFormGroup(this.vendor);

    const isSubscriber = !!this.vendor && !!this.vendor.subscriber;
    this.showSubscriberBadge = isSubscriber && this.authenticationStore.hasPermission('ViewGroupPayPropertyIndicators');

    this.parentForm.setControl('eventProperty', this.contactLookupForm);

    this.parentForm.setControl(`eventVendor-${this.getVendorCode(this.vendor)}`, this.contactLookupForm);

    if (this.authenticationStore.isSinglePropertyUser() && this.isNewEvent) {
      this.authenticationStore.user.pipe(take(1)).subscribe((user: User) => {
        const propertyQuery = new PropertyLookupDetailQuery({
          chain: user.allChains[0],
          brand: user.allBrands[0],
          propertyId: user.allProperties[0],
        });
        this.onSelectedProperty(propertyQuery);
      });
    }

    this.eventStore.selectedEvent.pipe(
      take(1)
    ).subscribe((selectedEvent: OnyxEvent) => {
      if (selectedEvent) {
        this.eventId = selectedEvent.eventId;
      }
    });

    this.eventValidationService.canRemoveVendor(this.vendor).pipe(
      take(1)
    ).subscribe((canRemoveVendor: boolean) => {
      this.canRemoveVendor = canRemoveVendor;
    });

    this.eventValidationService.canEditVendorContact(this.vendor).take(1).subscribe(canEditContact => {
      this.isContactEditable = this.authenticationStore.hasPermission('EditPropertyContact') && canEditContact;
    });

    this.eventValidationService.canEditVendorCommRate(this.vendor).pipe(
      take(1)
    ).subscribe((canEditVendorCommRate: boolean) => {
      this.canEditVendorCommRate = canEditVendorCommRate;
    });

    this.populateCardTitle();

    this.showLongLabels = !isSubscriber || (this.isNewEvent && this.authenticationStore.hasPermission('EditPropertyContact'));
  }

  ngOnDestroy() {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  onSelectedProperty(propertyQuery: PropertyLookupDetailQuery) {
    this.logService.debug(`propertySummary ${JSON.stringify(propertyQuery)}`);
    this.propertyLookupStore.getDetail(propertyQuery).pipe(take(1)).subscribe();
  }

  private dataToFormGroup(party: Party): FormGroup {
    if (!party) {
      party = new Party();
    } else {
      if (!party.contact) {
        party.contact = new Contact();
      }
      party = new Party(party);
    }

    if (!this.isNewEvent) {
      // onyxEvent chain/brand/propertyId is added to party if the event already existed
      party = party.merge({
        propertyId: party.propertyId,
        chain: party.chain,
        brand: party.brand
      }) as Party;
    }

    let refCodeValidator = [party.eventReferenceNumber, Validators.nullValidator];

    if (!this.contactLookupForm) {
      const emailValidator = !this.isReadOnly && this.authenticationStore.hasPermission('EditPropertyContact') ?
        [Validators.required, Validators.maxLength(75), PioneerValidators.isValidEmail] : Validators.nullValidator;

      this.contactLookupForm = this.formBuilder.group({
        propertyNameSearch: '',
        agencyNameSearch: '',
        name: party.name,
        line1: party.line1,
        line2: party.line2,
        partyType: PartyType.PROPERTY,
        payorType: party.payorType,
        city: party.city,
        state: party.state,
        postalCode: party.postalCode,
        agencyId: party.agencyId,
        code: party.code,
        propertyId: party.propertyId,
        chain: party.chain,
        brand: party.brand,
        eventReferenceNumber: refCodeValidator,
        manuallyAdded: party.manuallyAdded,
        autoGeneratedCode: party.autoGeneratedCode,
        countryCode: party.countryCode,
        commissionRoomPercentage: party.commissionRoomPercentage,
        commRetention: party.commRetention,
        contact: this.formBuilder.group({
          firstName: [party.contact.firstName],
          lastName: [party.contact.lastName],
          partyType: PartyType.PROPERTY,
          email: [party.contact.email, emailValidator],
          phone: [party.contact.phone],
          fax: [party.contact.fax]
        }),
        subscriber: party.subscriber
      }, {
        validator: PioneerValidators.isPropertyError
      });
    } else {
      this.contactLookupForm.patchValue({
        propertyNameSearch: '',
        agencyNameSearch: '',
        name: party.name,
        line1: party.line1,
        line2: party.line2,
        partyType: PartyType.PROPERTY,
        payorType: party.payorType,
        city: party.city,
        state: party.state,
        postalCode: party.postalCode,
        agencyId: party.agencyId,
        code: party.code,
        propertyId: party.propertyId,
        chain: party.chain,
        brand: party.brand,
        eventReferenceNumber: party.eventReferenceNumber,
        manuallyAdded: party.manuallyAdded,
        autoGeneratedCode: party.autoGeneratedCode,
        countryCode: party.countryCode,
        commissionRoomPercentage: party.commissionRoomPercentage,
        commRetention: party.commRetention
      });

      this.contactLookupForm.controls['contact'].patchValue({
        firstName: party.contact.firstName,
        lastName: party.contact.lastName,
        partyType: PartyType.PROPERTY,
        email: party.contact.email,
        phone: party.contact.phone,
        fax: party.contact.fax
      });

      if (party.contact.email) {
        this.outputEmail.emit(party.contact.email);
      }
    }

    return this.contactLookupForm;
  }

  setupPropertyEmail(email: string): void {
    this.outputEmail.emit(email);
  }

  confirmBeforeDelete($event: MouseEvent) {
    $event.preventDefault();
    this.confirmDeleteModal.open();
  }

  confirmHandler(deleteConfirmed: boolean) {
    if (deleteConfirmed) {
      this.deleteVendor.emit(this.vendor);
    }
  }

  private getVendorCode(vendor: Party): string {
    if (!vendor) {
      return '';
    }
    return vendor.code ? vendor.code : vendor.chain + '|' + vendor.brand + '|' + vendor.propertyId;
  }

  toggleCard(event: Event) {
    event.preventDefault();
    this.onToggleCard.emit();
  }

  private populateCardTitle() {
    if (!this.vendor) {
      return;
    }
    const isVendorProperty = this.vendor.partyType === PartyType.PROPERTY && this.vendor.payorType === 'PROPERTY';
    const validEventReferenceNumber = !!this.vendor.eventReferenceNumber;

    let groupCodePart = '';
    if (isVendorProperty && validEventReferenceNumber) {
      groupCodePart = this.translateService.instant('EVENT.GROUP_CODE');
      groupCodePart += `: ${this.vendor.eventReferenceNumber} `;
    }
    this.cardTitle = ` ${this.vendor.name} ${groupCodePart}`;
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/vendor-card-detail/vendor-card-detail.component.ts